// ContractDetail.js
import React, { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import {
  Box, Card, CardContent, Typography, Chip, Button,
  Divider, IconButton, Paper, Stack, useTheme,
  List, ListItem, ListItemIcon, ListItemText,
} from "@mui/material";
import {
  ArrowBack, Edit, LocationOn, Person, Email, Phone,
  Badge, CalendarToday, Description, AttachFile, OpenInNew,
} from "@mui/icons-material";
import { useDocument } from "../../hooks/useDocument";
import { useAuditFirestore } from "../../hooks/useAuditFirestore";
import LoadingSpinner from "../ui/LoadingSpinner";
import EmptyState from "../ui/EmptyState";
import { CONTRACT_STATUS, CONTRACT_STATUS_LABELS } from "../../utils/models/contractModel";
import { PROJECT_STATUS } from "../../utils/models/projectModel";

const formatAddress = (addr) => {
  if (!addr || !addr.street) return '—';
  const parts = [addr.street, addr.city, addr.province, addr.zipCode, addr.country].filter(Boolean);
  return parts.join(', ');
};

const ContractDetail = ({ contractId, onEdit }) => {
  const navigate = useNavigate();
  const theme = useTheme();
  const { add: addDoc } = useAuditFirestore("projects");
  const { document: contract, loading } = useDocument("contracts", contractId);
  const { update: updateContract } = useAuditFirestore("contracts");
  const [updating, setUpdating] = useState(false);

  const handleSign = async () => {
    setUpdating(true);
    try {
      await updateContract(contractId, {
        status: CONTRACT_STATUS.SIGNED,
        signedAt: new Date().toISOString(),
        updatedAt: new Date().toISOString(),
      });

      const projectData = {
        contractId: contract.id,
        contractTitle: contract.title,
        contractSignedDate: new Date().toISOString(),
        name: contract.title,
        description: `Proyecto generado desde el contrato: ${contract.title}`,
        clientId: contract.clientId,
        clientName: contract.clientName,
        clientPhone: contract.clientPhone || "",
        address: contract.address,
        status: PROJECT_STATUS.MANAGING,
        budget: 0,
        team: [],
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString(),
        createdBy: "",
        isActive: true,
      };

      await addDoc(projectData);
      navigate("/contracts");
    } catch (error) {
      console.error("❌ Error signing contract:", error);
    } finally {
      setUpdating(false);
    }
  };

  if (loading) return <LoadingSpinner />;
  if (!contract) return <EmptyState title="Contrato no encontrado" />;

  return (
    <Paper 
      elevation={0} 
      sx={{ 
        p: { xs: 2, md: 4 }, 
        borderRadius: { xs: "16px", sm: "24px" },
        border: "1px solid",
        borderColor: theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.12)' : '#e2e8f0', 
        background: theme.palette.background.paper,
      }}
    >
      {/* CABECERA */}
      <Box sx={{ display: "flex", alignItems: "flex-start", gap: { xs: 1, sm: 2 }, mb: { xs: 3, sm: 4 }, flexWrap: "wrap" }}>
        <IconButton 
          onClick={() => navigate("/contracts")} 
          sx={{ 
            background: theme.palette.background.paper, 
            border: "1px solid",
            borderColor: theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.12)' : '#e2e8f0', 
            mt: 0.5,
          }}
        >
          <ArrowBack sx={{ fontSize: { xs: 20, sm: 24 } }} />
        </IconButton>
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography 
            variant="h5" 
            fontWeight={800} 
            sx={{ 
              fontSize: { xs: "1.1rem", sm: "1.25rem", md: "1.5rem" },
              background: `linear-gradient(135deg, ${theme.palette.primary.main}, ${theme.palette.secondary.main})`,
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent',
              wordBreak: "break-word",
            }}
          >
            {contract.title}
          </Typography>
          <Stack direction="row" spacing={1} sx={{ mt: 1, flexWrap: "wrap", gap: 0.5 }}>
            <Chip
              label={CONTRACT_STATUS_LABELS[contract.status]}
              size="small"
              color={
                contract.status === CONTRACT_STATUS.SIGNED ? 'success' :
                contract.status === CONTRACT_STATUS.REJECTED ? 'error' :
                contract.status === CONTRACT_STATUS.SENT ? 'info' :
                contract.status === CONTRACT_STATUS.MODIFICATION ? 'secondary' :
                'warning'
              }
              variant="outlined"
              sx={{ fontWeight: 600, fontSize: { xs: "0.65rem", sm: "0.75rem" } }}
            />
            <Chip 
              label={contract.clientName} 
              size="small" 
              variant="outlined" 
              sx={{ borderColor: theme.palette.secondary.main, color: theme.palette.secondary.main, fontSize: { xs: "0.65rem", sm: "0.75rem" } }}
            />
            {contract.version && (
              <Chip
                label={`v${contract.version}`}
                size="small"
                color="primary"
                variant="outlined"
                sx={{ fontWeight: 600, fontSize: { xs: "0.65rem", sm: "0.75rem" } }}
              />
            )}
            {contract.proposalId && (
              <Chip
                label="📄 Ver propuesta"
                component={Link}
                to={`/proposals/${contract.proposalId}`}
                clickable
                size="small"
                color="primary"
                variant="outlined"
                sx={{ fontSize: { xs: "0.65rem", sm: "0.75rem" } }}
              />
            )}
          </Stack>
        </Box>
        <Stack direction="row" spacing={1}>
          {contract.status !== CONTRACT_STATUS.SIGNED && (
            <Button
              variant="contained"
              color="success"
              onClick={handleSign}
              disabled={updating}
              sx={{ borderRadius: "12px", height: { xs: 36, sm: 44 }, boxShadow: "none", fontSize: { xs: "0.75rem", sm: "0.85rem" } }}
            >
              {updating ? "Firmando..." : "Firmar contrato"}
            </Button>
          )}
        </Stack>
      </Box>

      {/* CONTENIDO PRINCIPAL */}
      <Box
        sx={{
          display: "flex",
          gap: { xs: 2, sm: 3 },
          flexWrap: "wrap",
          width: "100%",
          flexDirection: { xs: "column", md: "row" },
        }}
      >
        {/* INFORMACIÓN - LADO IZQUIERDO */}
        <Box
          sx={{
            flex: { md: "1 1 340px" },
            width: { xs: "100%", md: "auto" },
            minWidth: { md: "300px" },
          }}
        >
          <Card 
            elevation={0} 
            sx={{ 
              borderRadius: "16px", 
              border: "1px solid",
              borderColor: theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.12)' : '#e2e8f0', 
              background: theme.palette.background.paper, 
              height: "100%",
            }}
          >
            <CardContent sx={{ p: { xs: 2, sm: 3 } }}>
              <Typography 
                variant="subtitle1" 
                fontWeight={700} 
                sx={{ 
                  mb: 2, 
                  color: theme.palette.primary.main,
                  borderBottom: '2px solid',
                  borderColor: theme.palette.primary.main,
                  pb: 1,
                  display: 'block',
                  fontSize: { xs: "0.9rem", sm: "1rem" },
                }}
              >
                Información del cliente
              </Typography>

              <Typography variant="caption" color="text.secondary" fontWeight={600} sx={{ fontSize: { xs: "0.7rem", sm: "0.75rem" } }}>
                Cliente
              </Typography>
              <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 2, mt: 0.5 }}>
                <Person sx={{ fontSize: { xs: 16, sm: 18 }, color: theme.palette.primary.main }} />
                <Typography variant="body2" sx={{ fontSize: { xs: "0.8rem", sm: "0.85rem" } }}>{contract.clientName || "—"}</Typography>
              </Box>

              {contract.clientDni && (
                <>
                  <Typography variant="caption" color="text.secondary" fontWeight={600} sx={{ fontSize: { xs: "0.7rem", sm: "0.75rem" } }}>
                    DNI / CIF
                  </Typography>
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 2, mt: 0.5 }}>
                    <Badge sx={{ fontSize: { xs: 16, sm: 18 }, color: theme.palette.primary.main }} />
                    <Typography variant="body2" sx={{ fontSize: { xs: "0.8rem", sm: "0.85rem" } }}>{contract.clientDni}</Typography>
                  </Box>
                </>
              )}

              {contract.clientEmail && (
                <>
                  <Typography variant="caption" color="text.secondary" fontWeight={600} sx={{ fontSize: { xs: "0.7rem", sm: "0.75rem" } }}>
                    Email
                  </Typography>
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 2, mt: 0.5 }}>
                    <Email sx={{ fontSize: { xs: 16, sm: 18 }, color: theme.palette.primary.main }} />
                    <Typography variant="body2" sx={{ fontSize: { xs: "0.8rem", sm: "0.85rem" }, wordBreak: "break-all" }}>{contract.clientEmail}</Typography>
                  </Box>
                </>
              )}

              {contract.clientPhone && (
                <>
                  <Typography variant="caption" color="text.secondary" fontWeight={600} sx={{ fontSize: { xs: "0.7rem", sm: "0.75rem" } }}>
                    Teléfono
                  </Typography>
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 2, mt: 0.5 }}>
                    <Phone sx={{ fontSize: { xs: 16, sm: 18 }, color: theme.palette.success.main }} />
                    <Typography variant="body2" sx={{ fontSize: { xs: "0.8rem", sm: "0.85rem" } }}>{contract.clientPhone}</Typography>
                  </Box>
                </>
              )}

              {/* ARCHIVOS */}
              {contract.files && contract.files.length > 0 && (
                <>
                  <Divider sx={{ my: 2, borderColor: theme.palette.divider }} />
                  <Typography
                    variant="subtitle2"
                    fontWeight={700}
                    sx={{ 
                      mb: 2,
                      color: theme.palette.primary.main,
                      display: 'flex',
                      alignItems: 'center',
                      gap: 1,
                      fontSize: { xs: "0.85rem", sm: "0.9rem" },
                    }}
                  >
                    <AttachFile sx={{ fontSize: { xs: 18, sm: 20 } }} />
                    Archivos adjuntos
                  </Typography>
                  <List dense disablePadding>
                    {contract.files.map((file, index) => (
                      <ListItem key={index} sx={{ px: 0, py: 0.5 }}>
                        <ListItemIcon sx={{ minWidth: 36 }}>
                          <Description sx={{ fontSize: { xs: 18, sm: 20 }, color: theme.palette.info.main }} />
                        </ListItemIcon>
                        <ListItemText>
                          <Link
                            href={file.url}
                            target="_blank"
                            rel="noopener noreferrer"
                            underline="hover"
                            sx={{
                              color: theme.palette.primary.main,
                              fontWeight: 500,
                              fontSize: { xs: "0.8rem", sm: "0.85rem" },
                              display: 'flex',
                              alignItems: 'center',
                              gap: 0.5,
                              cursor: 'pointer',
                              wordBreak: 'break-all',
                              '&:hover': {
                                color: theme.palette.primary.dark,
                              },
                            }}
                          >
                            {file.name || file.url}
                            <OpenInNew sx={{ fontSize: 14 }} />
                          </Link>
                        </ListItemText>
                      </ListItem>
                    ))}
                  </List>
                </>
              )}

              {contract.version && (
                <>
                  <Divider sx={{ my: 2, borderColor: theme.palette.divider }} />
                  <Typography variant="caption" color="text.secondary" fontWeight={600} sx={{ fontSize: { xs: "0.7rem", sm: "0.75rem" } }}>
                    Versión
                  </Typography>
                  <Typography variant="body2" fontWeight={600} color="primary" sx={{ mt: 0.5, fontSize: { xs: "0.8rem", sm: "0.85rem" } }}>
                    v{contract.version}
                  </Typography>
                  {contract.versions?.length > 0 && (
                    <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 0.5, fontSize: { xs: "0.7rem", sm: "0.75rem" } }}>
                      {contract.versions.length} cambio(s) registrado(s)
                    </Typography>
                  )}
                </>
              )}

              <Divider sx={{ my: 2, borderColor: theme.palette.divider }} />

              <Typography 
                variant="subtitle2" 
                fontWeight={700} 
                sx={{ 
                  mb: 2, 
                  display: "flex", 
                  alignItems: "center", 
                  gap: 1,
                  color: theme.palette.primary.main,
                  fontSize: { xs: "0.8rem", sm: "0.875rem" },
                }}
              >
                <LocationOn sx={{ fontSize: { xs: 16, sm: 18 }, color: theme.palette.error.main }} />
                Dirección de instalación
              </Typography>

              {contract.address?.street && (
                <>
                  <Typography variant="caption" color="text.secondary" fontWeight={600} sx={{ fontSize: { xs: "0.7rem", sm: "0.75rem" } }}>
                    Calle
                  </Typography>
                  <Typography variant="body2" sx={{ mb: 1.5, fontSize: { xs: "0.8rem", sm: "0.85rem" } }}>{contract.address.street}</Typography>
                </>
              )}

              <Box sx={{ display: "flex", gap: 1.5, flexWrap: "wrap" }}>
                {contract.address?.city && (
                  <Box sx={{ flex: 1, minWidth: "120px" }}>
                    <Typography variant="caption" color="text.secondary" fontWeight={600} sx={{ fontSize: { xs: "0.7rem", sm: "0.75rem" } }}>Población</Typography>
                    <Typography variant="body2" sx={{ fontSize: { xs: "0.8rem", sm: "0.85rem" } }}>{contract.address.city}</Typography>
                  </Box>
                )}
                {contract.address?.province && (
                  <Box sx={{ flex: 1, minWidth: "120px" }}>
                    <Typography variant="caption" color="text.secondary" fontWeight={600} sx={{ fontSize: { xs: "0.7rem", sm: "0.75rem" } }}>Provincia</Typography>
                    <Typography variant="body2" sx={{ fontSize: { xs: "0.8rem", sm: "0.85rem" } }}>{contract.address.province}</Typography>
                  </Box>
                )}
              </Box>

              <Box sx={{ display: "flex", gap: 1.5, flexWrap: "wrap", mt: 1.5 }}>
                {contract.address?.zipCode && (
                  <Box sx={{ flex: 1, minWidth: "120px" }}>
                    <Typography variant="caption" color="text.secondary" fontWeight={600} sx={{ fontSize: { xs: "0.7rem", sm: "0.75rem" } }}>Código Postal</Typography>
                    <Typography variant="body2" sx={{ fontSize: { xs: "0.8rem", sm: "0.85rem" } }}>{contract.address.zipCode}</Typography>
                  </Box>
                )}
                {contract.address?.country && (
                  <Box sx={{ flex: 1, minWidth: "120px" }}>
                    <Typography variant="caption" color="text.secondary" fontWeight={600} sx={{ fontSize: { xs: "0.7rem", sm: "0.75rem" } }}>País</Typography>
                    <Typography variant="body2" sx={{ fontSize: { xs: "0.8rem", sm: "0.85rem" } }}>{contract.address.country}</Typography>
                  </Box>
                )}
              </Box>

              {contract.signedAt && (
                <>
                  <Divider sx={{ my: 2, borderColor: theme.palette.divider }} />
                  <Typography variant="caption" color="text.secondary" fontWeight={600} sx={{ fontSize: { xs: "0.7rem", sm: "0.75rem" } }}>Firmado</Typography>
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1, mt: 0.5 }}>
                    <CalendarToday sx={{ fontSize: { xs: 16, sm: 18 }, color: theme.palette.success.main }} />
                    <Typography variant="body2" sx={{ fontSize: { xs: "0.8rem", sm: "0.85rem" } }}>
                      {new Date(contract.signedAt).toLocaleDateString('es-ES', { year: 'numeric', month: 'long', day: 'numeric' })}
                    </Typography>
                  </Box>
                </>
              )}

              {contract.notes && (
                <>
                  <Divider sx={{ my: 2, borderColor: theme.palette.divider }} />
                  <Typography variant="caption" color="text.secondary" fontWeight={600} sx={{ fontSize: { xs: "0.7rem", sm: "0.75rem" } }}>Notas</Typography>
                  <Typography variant="body2" sx={{ mt: 0.5, whiteSpace: 'pre-wrap', fontSize: { xs: "0.8rem", sm: "0.85rem" } }}>{contract.notes}</Typography>
                </>
              )}
            </CardContent>
          </Card>
        </Box>

        {/* DESCRIPCIÓN Y DETALLES - LADO DERECHO */}
        <Box
          sx={{
            flex: { md: "2 1 540px" },
            width: { xs: "100%", md: "auto" },
            minWidth: { md: "350px" },
            display: "flex",
            flexDirection: "column",
            gap: { xs: 2, sm: 3 },
          }}
        >
          <Card elevation={0} sx={{ borderRadius: "16px", border: "1px solid", borderColor: theme.palette.divider, background: theme.palette.background.paper }}>
            <CardContent sx={{ p: { xs: 2, sm: 3 } }}>
              <Typography variant="subtitle1" fontWeight={700} sx={{ mb: 2, color: theme.palette.primary.main, display: "flex", alignItems: "center", gap: 1, fontSize: { xs: "0.9rem", sm: "1rem" } }}>
                <Description sx={{ fontSize: { xs: 18, sm: 20 } }} />Descripción del contrato
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ whiteSpace: 'pre-wrap', fontSize: { xs: "0.8rem", sm: "0.85rem" } }}>
                {contract.description || "Sin descripción"}
              </Typography>
            </CardContent>
          </Card>

          <Card elevation={0} sx={{ borderRadius: "16px", border: "1px solid", borderColor: theme.palette.divider, background: theme.palette.background.paper }}>
            <CardContent sx={{ p: { xs: 2, sm: 3 } }}>
              <Typography variant="subtitle1" fontWeight={700} sx={{ mb: 2, color: theme.palette.primary.main, fontSize: { xs: "0.9rem", sm: "1rem" } }}>Propuesta relacionada</Typography>
              {contract.proposalId ? (
                <Box sx={{ p: 2, bgcolor: theme.palette.mode === 'dark' ? 'rgba(0, 175, 163, 0.1)' : 'rgba(0, 95, 101, 0.03)', borderRadius: "12px", border: "1px solid", borderColor: theme.palette.divider }}>
                  <Link to={`/proposals/${contract.proposalId}`} style={{ textDecoration: "none" }}>
                    <Typography variant="body2" color="primary" fontWeight={500} sx={{ fontSize: { xs: "0.8rem", sm: "0.85rem" } }}>
                      📄 {contract.proposalTitle || "Ver propuesta"}
                    </Typography>
                  </Link>
                </Box>
              ) : (
                <Typography variant="body2" color="text.secondary" sx={{ fontSize: { xs: "0.8rem", sm: "0.85rem" } }}>No hay propuesta relacionada</Typography>
              )}
            </CardContent>
          </Card>

          <Card elevation={0} sx={{ borderRadius: "16px", border: "1px solid", borderColor: theme.palette.divider, background: theme.palette.background.paper }}>
            <CardContent sx={{ p: { xs: 2, sm: 3 } }}>
              <Typography variant="subtitle1" fontWeight={700} sx={{ mb: 2, color: theme.palette.primary.main, fontSize: { xs: "0.9rem", sm: "1rem" } }}>Resumen de dirección</Typography>
              <Box sx={{ p: 2, bgcolor: theme.palette.mode === 'dark' ? 'rgba(0, 175, 163, 0.1)' : 'rgba(0, 95, 101, 0.03)', borderRadius: "12px", border: "1px solid", borderColor: theme.palette.divider }}>
                <Typography variant="body1" sx={{ fontSize: { xs: "0.8rem", sm: "0.9rem" } }}>{formatAddress(contract.address)}</Typography>
              </Box>
            </CardContent>
          </Card>
        </Box>
      </Box>
    </Paper>
  );
};

export default ContractDetail;